// Settings — the home for everything the new shell did not replace.
//
// The keep-list from PLAN-03 U7 (the dev dashboard, notify, sfx, the account and
// redeem panels, the Parent Corner) all needed somewhere to live once the old
// overlay went, and this is it. The settings themselves are written in the
// shell's own markup; the logic behind the rows is the existing modules'.

import { getLocale, setLocale, t } from '../../i18n';
import { SupabaseClient, accountsEnabled } from '../../net/supabase';
import { redeemCode } from '../../net/redemption';
import { accountControls, completeSignIn } from '../../ui/accountPanel';
import { getItem } from '../../sim/items';
import { CITY_PRESETS, geocodeCity, type Coordinates } from '../../sim/weather';
import { hashPin, clearPinTries, pinGate, recordPinMiss } from '../../ui/parentCorner';
import { readinessMetrics, TRIAL_DAYS } from '../../sim/progression';
import { leagueForecast } from '../../sim/forecast';
import { button, div, h, icon, paint } from '../dom';
import { openSheet } from '../components/sheet';
import { notificationsRow, notificationsScreen } from './notifications';
import type { Screen, ScreenContext } from '../router';

const GEAR_ICON =
  '<circle cx="12" cy="12" r="3"></circle><path d="M12 2v3"></path><path d="M12 19v3"></path><path d="M2 12h3"></path><path d="M19 12h3"></path><path d="m5 5 2 2"></path><path d="m17 17 2 2"></path><path d="m19 5-2 2"></path><path d="m7 17-2 2"></path>';

export function settingsScreen(): Screen<HTMLElement> {
  return {
    id: 'settings',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, game } = ctx.services;
      const client = new SupabaseClient();

      const rows = div({ class: 'cf-stack cf-stack--tight' });
      const screen = div(
        { class: 'cf-screen' },
        h('h1', { class: 'cf-display', text: t('settings.title') }),
        rows,
        div(
          { class: 'cf-screen__foot' },
          button({
            class: 'cf-btn cf-btn--primary',
            text: t('common.back'),
            on: { click: () => ctx.router.back() },
          }),
        ),
      );

      const paintRows = (): void => {
        const state = store.getState();
        paint(rows, `${state.settings.locale}|${state.settings.soundEnabled}|${String(state.settings.reducedMotion)}|${state.settings.weatherCity ?? ''}`, () =>
          div(
            { class: 'cf-stack cf-stack--tight' },
            localeRow(),
            switchRow('settings.sound', state.settings.soundEnabled, () => {
              store.mutate((s) => {
                s.settings.soundEnabled = !s.settings.soundEnabled;
              });
              paintRows();
            }),
            switchRow('settings.reducedMotion', state.settings.reducedMotion ?? false, () => {
              store.mutate((s) => {
                s.settings.reducedMotion = !(s.settings.reducedMotion ?? false);
              });
              paintRows();
            }),
            // A league save's weather is the season's, so there is no town to
            // pick and no coordinates to fetch: the row goes rather than sitting
            // there accepting a choice the sim would ignore (sim/forecast.ts).
            leagueForecast(state)
              ? null
              : linkRow('settings.weather', t('weather.cityCurrent', { city: weatherCityLabel(store.getState().settings.weatherCity) }), () =>
                  openWeatherSheet(ctx),
                ),
            linkRow('settings.parentCorner', t('parent.subtitle'), () => openParentSheet(ctx)),
            // Kept as a row in every build: with accounts off the sheet says so
            // instead of hiding, so the switch is visible rather than silent.
            linkRow('settings.account', t('account.guestFirst'), () => openAccountSheet(ctx, client)),
            // Push is a route, not a sheet: it is a standing setting about the
            // player's own phone, with Back meaning what it says.
            notificationsRow(() => ctx.router.push(notificationsScreen())),
            import.meta.env.DEV
              ? linkRow('settings.devTools', t('settings.devToolsSub'), () => game.toggleDevDashboard())
              : null,
          ),
        );
      };

      function localeRow(): HTMLElement {
        const current = store.getState().settings.locale;
        const next = current === 'en' ? 'es' : 'en';
        // The row says which language the game is in, not which one a tap would
        // switch to — "Language: Spanish" on an English screen is a lie.
        return linkRow(
          'settings.language',
          current === 'en' ? t('settings.languageEn') : t('settings.languageEs'),
          () => {
            store.mutate((s) => {
              s.settings.locale = next;
            });
            setLocale(next);
            // A language switch re-renders every panel, and the simplest
            // correct move is a reload — the choice is on disk first, or the
            // reload would load the old save and silently undo it.
            void store.save().then(() => window.location.reload());
          },
        );
      }

      paintRows();
      screen.tabIndex = -1;
      return screen;
    },
  };
}

/** The settings button that lives in the coop top bar. */
export function settingsButton(onOpen: () => void): HTMLButtonElement {
  return button(
    {
      class: 'cf-btn cf-btn--icon',
      attrs: { 'aria-label': t('settings.title') },
      on: { click: onOpen },
    },
    icon({ shapes: GEAR_ICON, size: 22 }),
  );
}

function switchRow(labelKey: string, on: boolean, onToggle: () => void): HTMLElement {
  const row = button(
    {
      class: 'cf-toggle',
      attrs: { 'aria-pressed': String(on) },
      on: { click: onToggle },
    },
    h('span', { class: 'cf-toggle__label cf-grow', text: t(labelKey) }),
    h('span', { class: 'cf-switch' }),
  );
  if (on) row.classList.add('cf-toggle--on');
  return row;
}

function linkRow(labelKey: string, sub: string, onOpen: () => void): HTMLElement {
  return button(
    { class: 'cf-row', on: { click: onOpen } },
    h(
      'span',
      { class: 'cf-stack cf-stack--tight cf-grow' },
      h('span', { class: 'cf-label', text: t(labelKey) }),
      h('span', { class: 'cf-small cf-muted', text: sub }),
    ),
  );
}

/** No city picked means the local fallback, which says so rather than guessing. */
function weatherCityLabel(saved: string | undefined): string {
  return saved ?? t('weather.cityNone');
}

/**
 * The city picker — the only way weather gets a location now that the game no
 * longer asks the device for a position (COPPA-NOTES.md §D1). Presets first,
 * then a typed city; either way the save keeps the name.
 */
function openWeatherSheet(ctx: ScreenContext<HTMLElement>): void {
  const { shell, game } = ctx.services;
  const sheet = openSheet(shell, { title: t('weather.cityTitle'), onClose: () => undefined });
  const status = h('p', { class: 'cf-small cf-muted', text: t('weather.cityBlurb') });

  const choose = (city: Coordinates | null): void => {
    void game.pickWeatherCity(city?.label ?? null).then(() => {
      status.textContent = city ? t('weather.cityPicked', { city: city.label }) : t('weather.cityNone');
    });
  };

  const search = h('input', {
    class: 'cf-input',
    attrs: {
      type: 'search',
      placeholder: t('weather.citySearch'),
      'aria-label': t('weather.citySearch'),
    },
  });

  const list = div(
    { class: 'cf-stack cf-stack--tight' },
    ...CITY_PRESETS.map((city) =>
      button(
        { class: 'cf-row', on: { click: () => choose(city) } },
        h('span', { class: 'cf-label cf-grow', text: city.label }),
      ),
    ),
    button(
      { class: 'cf-row', on: { click: () => choose(null) } },
      h('span', { class: 'cf-label cf-grow', text: t('weather.cityNone') }),
    ),
  );

  sheet.body.replaceChildren(
    h('p', { class: 'cf-sub', text: t('weather.cityBlurb') }),
    search,
    list,
    button({
      class: 'cf-btn cf-btn--outline',
      text: t('weather.citySearchAction'),
      on: {
        click: () => {
          void (async () => {
            const value = (search as HTMLInputElement).value.trim();
            if (!value) {
              status.textContent = t('weather.cityNotFound');
              return;
            }
            const hit = await geocodeCity(value).catch(() => null);
            if (!hit) {
              status.textContent = t('weather.cityNotFound');
              return;
            }
            choose(hit);
          })();
        },
      },
    }),
    status,
  );
}

/**
 * The Parent Corner, in the shell's markup. The gate is the real one —
 * `hashPin` and the 5-try cooldown from src/ui/parentCorner.ts — and so is the
 * data, `readinessMetrics`. Only the rendering is new.
 */
function openParentSheet(ctx: ScreenContext<HTMLElement>): void {
  const { store, shell } = ctx.services;
  const sheet = openSheet(shell, { title: t('parent.title'), onClose: () => undefined });
  const body = div({ class: 'cf-stack' });
  sheet.body.replaceChildren(body);

  const showGate = (): void => {
    const existing = store.getState().settings.parentPinHash;
    const input = h('input', {
      class: 'cf-input',
      attrs: {
        type: 'password',
        inputmode: 'numeric',
        maxlength: '4',
        'aria-label': t('parent.pinPrompt'),
        placeholder: t('parent.pinPrompt'),
      },
    });
    const error = div({ class: 'cf-notice cf-notice--danger' });
    error.style.display = 'none';
    const errorText = h('span', { class: 'cf-notice__text', text: t('parent.pinWrong') });
    error.appendChild(errorText);

    const unlock = button({
      class: 'cf-btn cf-btn--primary',
      text: t('parent.unlock'),
      on: {
        click: () => {
          void (async () => {
            if (pinGate().lockedUntil > Date.now()) {
              showLocked();
              return;
            }
            const hash = await hashPin((input as HTMLInputElement).value);
            if (!existing) {
              store.mutate((s) => {
                s.settings.parentPinHash = hash;
              });
              clearPinTries();
              showDashboard();
              return;
            }
            if (hash === existing) {
              clearPinTries();
              showDashboard();
            } else {
              recordPinMiss();
              error.style.display = '';
              errorText.textContent = t('parent.pinWrong');
              shell.toast(t('parent.pinWrong'));
            }
          })();
        },
      },
    });

    /** Mid-cooldown the sheet says so and offers no way to guess past it. */
    const showLocked = (): void => {
      error.style.display = '';
      errorText.textContent = t('parent.pinLocked');
      unlock.disabled = true;
    };

    body.replaceChildren(
      h('p', { class: 'cf-sub', text: existing ? t('parent.pinPrompt') : t('parent.pinSet') }),
      input,
      error,
      unlock,
    );

    if (pinGate().lockedUntil > Date.now()) showLocked();
  };

  const showDashboard = (): void => {
    const metrics = readinessMetrics(store.getState());
    const rows: Array<[string, string]> = [
      [t('parent.streak'), `${metrics.careStreak} ${t('common.days')}`],
      [t('parent.promptness'), `${metrics.promptnessPercent}%`],
      [t('parent.unassisted'), `${metrics.unassistedPercent}%`],
      [t('parent.incidents'), String(metrics.incidents)],
      [t('parent.trialWindow'), `${metrics.daysTracked}/${TRIAL_DAYS} ${t('common.days')}`],
    ];
    body.replaceChildren(
      h('p', { class: 'cf-sub', text: t('parent.subtitle') }),
      div(
        { class: 'cf-cost' },
        ...rows.map(([label, value]) =>
          h('div', { class: 'cf-cost__item' }, h('span', { class: 'cf-grow', text: label }), h('strong', { text: value })),
        ),
      ),
    );
  };

  showGate();
}

/** One sentence per outcome, from the table the old panel used. */
function describe(outcome: Awaited<ReturnType<typeof redeemCode>>): string {
  switch (outcome.status) {
    case 'redeemed': {
      const grant = outcome.granted;
      if (grant.kind === 'eggits') return t('redeem.successEggits', { count: grant.amount });
      if (grant.kind === 'item') {
        return t('redeem.successItem', {
          count: grant.amount,
          item: getItem(grant.itemId)?.name[locale()] ?? grant.itemId,
        });
      }
      if (grant.kind === 'bird') {
        return t('redeem.successBird', {
          name: grant.bird.name,
          // The event's own name comes from the server, not from a table here.
          event: outcome.payload.eventLabel,
          stage: t(`flock.stage.${grant.bird.stage}`),
        });
      }
      return t('redeem.success', {
        breed: t(`geneticsAlbum.breed.${grant.egg.breed}`),
        event: grant.egg.eventLabel,
      });
    }
    case 'rejected':
      return t(`code.${outcome.reason}`);
    case 'failed':
      return t(`code.${outcome.reason}`);
    case 'needs_account':
      return t('redeem.needsAccount');
    case 'unavailable':
      return t('redeem.unavailable');
  }
}

function locale(): 'en' | 'es' {
  return getLocale() === 'es' ? 'es' : 'en';
}

/**
 * The family account and the redeem code. Guest-first is law (AUTH-BACKEND.md
 * §1) and the sheet says so before it asks for anything. The sign-in and
 * upgrade logic is `completeSignIn`, unchanged.
 */
function openAccountSheet(ctx: ScreenContext<HTMLElement>, client: SupabaseClient): void {
  const { store, shell } = ctx.services;
  const sheet = openSheet(shell, { title: t('parent.title'), onClose: () => undefined });
  const status = h('p', { class: 'cf-small cf-muted', text: t('account.guestFirst') });

  // This sheet is the signed-OUT surface — email, magic link, redeem code —
  // so which controls it may render is the same decision the account panel
  // makes, asked for a signed-out player. With accounts off there is no email
  // field, no send button and no redeem box, and the sheet says why instead of
  // leaving a child to find out by tapping.
  const controls = accountControls({ enabled: accountsEnabled(), signedIn: false });

  const intro = [
    h('p', { class: 'cf-sub', text: t('account.guestFirst') }),
    h('p', { class: 'cf-small', text: t('account.emailNote') }),
  ];

  if (!controls.includes('email')) {
    sheet.body.replaceChildren(
      ...intro,
      h('p', { class: 'cf-sub', text: t('account.notConfigured') }),
      status,
    );
    return;
  }

  const email = h('input', {
    class: 'cf-input',
    attrs: { type: 'email', autocomplete: 'email', placeholder: t('account.email') },
  });
  const code = h('input', {
    class: 'cf-input',
    attrs: { type: 'text', autocapitalize: 'characters', placeholder: t('redeem.code') },
  });
  sheet.body.replaceChildren(
    ...intro,
    email,
    button({
      class: 'cf-btn cf-btn--primary',
      text: t('account.sendLink'),
      on: {
        click: () => {
          void (async () => {
            const value = (email as HTMLInputElement).value;
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              status.textContent = t('account.badEmail');
              return;
            }
            const result = await client.sendMagicLink(value, store.getState().settings.locale);
            // Online is optional: with no config the client reports
            // `not_configured` and the game plays exactly as before.
            status.textContent = result.ok
              ? t('account.linkSent')
              : result.error === 'not_configured'
                ? t('account.guestFirst')
                : t('account.linkFailed');
            if (result.ok) await completeSignIn(store, client, (m) => (status.textContent = m));
          })();
        },
      },
    }),
    h('p', { class: 'cf-small', text: t('redeem.blurb') }),
    code,
    button(
      {
        class: 'cf-btn cf-btn--outline',
        text: t('redeem.submit'),
        on: {
          click: () => {
            void (async () => {
              const value = (code as HTMLInputElement).value.trim().toUpperCase();
              if (value.length === 0) {
                status.textContent = t('redeem.enterCode');
                return;
              }
              status.textContent = t('redeem.checking', { code: value });
              // The real call. Every outcome the server can produce has a
              // sentence in the table, so a refusal is never a silent nothing
              // and never a leaked code string.
              const outcome = await redeemCode(store, client, value);
              status.textContent = describe(outcome);
            })();
          },
        },
      },
    ),
    status,
  );
}
